<script setup lang="ts">
// HLOS-300+: LXShell — dispatcher genérico para renderers de Learning Experience.
//
// HLOS-249 (Vue renderers) — recebe `lx` (LXState) e seleciona renderer baseado em `lx.lx_type`.
// Todos os renderers são lazy-loaded via defineAsyncComponent (code-split per HLOS-240).
//
// AC-9 (a11y): aria-live="polite" + keyboard nav + reduced motion.

import { defineAsyncComponent, computed } from 'vue'
import type { LXState, LXInteractionEvent } from '../types'

const props = defineProps<{
  lx: LXState | null
}>()

const emit = defineEmits<{
  interaction: [event: LXInteractionEvent]
  abandon: [instanceId: string]
  complete: [state: LXState]
}>()

// HLOS-249: renderers lazy-loaded (code-split per LX type — plano §4.7)
const QuestionRenderer = defineAsyncComponent(
  () => import('./specific/QuestionRenderer.vue'),
)
const QuizRenderer = defineAsyncComponent(
  () => import('./specific/QuizRenderer.vue'),
)
const FlashcardRenderer = defineAsyncComponent(
  () => import('./specific/FlashcardRenderer.vue'),
)
const CrosswordRenderer = defineAsyncComponent(
  () => import('./specific/CrosswordRenderer.vue'),
)

// HLOS-249: dispatcher baseado em lx.lx_type
const activeRenderer = computed(() => {
  if (!props.lx) return null
  switch (props.lx.lx_type) {
    case 'question':
      return QuestionRenderer
    case 'quiz':
      return QuizRenderer
    case 'flashcard':
      return FlashcardRenderer
    case 'crossword':
      return CrosswordRenderer
    default:
      return null
  }
})

function handleInteraction(event: LXInteractionEvent) {
  emit('interaction', event)
}

function handleAbandon(instanceId: string) {
  emit('abandon', instanceId)
}

function handleComplete(state: LXState) {
  emit('complete', state)
}
</script>

<template>
  <div class="lx-shell" :data-lx-type="lx?.lx_type">
    <!-- HLOS-249: a11y — aria-live="polite" para feedback de progresso -->
    <div
      v-if="lx"
      class="lx-renderer"
      role="region"
      :aria-label="`Learning experience: ${lx.lx_type}`"
    >
      <component
        :is="activeRenderer"
        v-if="activeRenderer"
        :lx="lx"
        @interaction="handleInteraction"
        @abandon="handleAbandon"
        @complete="handleComplete"
      />
      <p v-else aria-live="polite">Carregando LX...</p>
<<<<<<< HEAD
=======
<<<<<<< HEAD
=======
    </div>
>>>>>>> origin/dev
>>>>>>> origin/dev
  </div>
</template>

<style scoped>
.lx-shell {
  /* HLOS-249: a11y — prefers-reduced-motion respeitada */
  background: var(--hlos-color-bg-secondary, #f4f1ec);
  border-radius: var(--hlos-radius-md, 8px);
  padding: var(--hlos-spacing-md, 16px);
}
.lx-renderer {
  min-height: 200px;
}
@media (prefers-reduced-motion: reduce) {
  .lx-shell {
    /* sem loading spinners para usuários com prefers-reduced-motion */
    transition: none;
  }
}
</style>